// sessionRows.ts — pure list and record reducers for the Sessions page.
//
//   insertIfAbsent  a created row, unless the list already has it.
//   upsertById      replace a row in place, or prepend it if new.
//   replaceById     splice a returned row over its old copy (no insert).
//   removeById      drop a row.
//   withoutKey      a record minus one key (title drafts).
//   readyKey        the ready sessions as one stable string, so the
//                   harness sockets reconcile only when the set changes.
//   appendBootLog   add a pod log line, collapsing repeats and capping
//                   the total.
//   toggleInSet     flip one id in a set (the MCP enable-set draft).
//
// Rows arrive whole rather than as patches, so applying one by id cannot
// drift. Every function returns a new value and never mutates its input.

export interface HasId {
  id: string;
}

/** A created row, unless a row with its id is already present. */
export function insertIfAbsent<T extends HasId>(prev: T[], row: T): T[] {
  return prev.some((r) => r.id === row.id) ? prev : [row, ...prev];
}

/** Replace the row with `row`'s id in place, or prepend it when absent. */
export function upsertById<T extends HasId>(prev: T[], row: T): T[] {
  return prev.some((r) => r.id === row.id)
    ? prev.map((r) => (r.id === row.id ? row : r))
    : [row, ...prev];
}

/**
 * Splice a row the server returned over its old copy. Never inserts:
 * a full list reload here would race the turn-end title regen and
 * flicker the row labels, so callers splice rather than refetch.
 */
export function replaceById<T extends HasId>(prev: T[], row: T): T[] {
  return prev.map((r) => (r.id === row.id ? row : r));
}

/** Drop the row with `id`. */
export function removeById<T extends HasId>(prev: T[], id: string): T[] {
  return prev.filter((r) => r.id !== id);
}

/** A copy of `rec` without `key`. */
export function withoutKey<V>(rec: Record<string, V>, key: string): Record<string, V> {
  const next = { ...rec };
  delete next[key];
  return next;
}

/**
 * The ids of the sessions whose pod is ready, sorted and comma-joined.
 *
 * Keyed on a joined string rather than the array so a re-render that
 * rebuilt the same list doesn't cycle every harness connection. Only the
 * ready ones: a suspended or provisioning session has no Pod to reach.
 */
export function readyKey(
  sessions: readonly HasId[],
  phases: Record<string, string | undefined>,
): string {
  return sessions.filter((s) => phases[s.id] === 'ready').map((s) => s.id).sort().join(',');
}

/** One row of the pod boot log, with how many times it repeated. */
export interface BootLogLine {
  text: string;
  count: number;
}

/** The most distinct rows the boot log keeps. */
export const BOOT_LOG_CAP = 500;

/**
 * Append a pod log line. A run of identical lines collapses into one row
 * with a count — kubelet re-emits the same status once per poll while a
 * slow image pull drags on, and hundreds of copies would bury the pane.
 * Distinct rows are capped at BOOT_LOG_CAP so verbose pull progress can't
 * balloon the DOM.
 */
export function appendBootLog(prev: BootLogLine[], line: string): BootLogLine[] {
  const last = prev[prev.length - 1];
  if (last && last.text === line) {
    const next = prev.slice();
    next[next.length - 1] = { text: last.text, count: last.count + 1 };
    return next;
  }
  const next = prev.length >= BOOT_LOG_CAP ? prev.slice(-(BOOT_LOG_CAP - 1)) : prev.slice();
  next.push({ text: line, count: 1 });
  return next;
}

/** A copy of `prev` with `id` added if absent and removed if present. */
export function toggleInSet(prev: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(prev);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
}
